import type { IAxisZoomBounds, IViewportZoomBounds } from "./types";

export type ISelectionMode = "x" | "y" | "xy";

export interface ISelectionRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface IRadialScaleLike {
  xCenter: number;
  yCenter: number;
  drawingArea: number;
  min?: number;
  max?: number;
  getValueForDistanceFromCenter: (distance: number) => number;
}

export interface IViewportAreaLike {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface IViewportTransform {
  scale: number;
  translateX: number;
  translateY: number;
}

export interface IViewportPoint {
  x: number;
  y: number;
}

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export const normalizeViewportBounds = (viewport: IViewportZoomBounds | undefined): IViewportZoomBounds | null => {
  if (!viewport) {
    return null;
  }

  const bounds = [viewport.left, viewport.right, viewport.top, viewport.bottom];

  if (bounds.some(bound => !Number.isFinite(bound))) {
    return null;
  }

  const left = clamp(Math.min(viewport.left, viewport.right), 0, 1);
  const right = clamp(Math.max(viewport.left, viewport.right), 0, 1);
  const top = clamp(Math.min(viewport.top, viewport.bottom), 0, 1);
  const bottom = clamp(Math.max(viewport.top, viewport.bottom), 0, 1);

  if (left >= right || top >= bottom) {
    return null;
  }

  return { left, right, top, bottom };
};

export const getViewportScale = (viewport: IViewportZoomBounds): number | null => {
  const viewportSpan = Math.max(viewport.right - viewport.left, viewport.bottom - viewport.top);

  if (!Number.isFinite(viewportSpan) || viewportSpan <= 0 || viewportSpan >= 1) {
    return null;
  }

  return 1 / viewportSpan;
};

export const getViewportTransform = (
  viewportArea: IViewportAreaLike,
  viewport: IViewportZoomBounds | undefined,
): IViewportTransform | null => {
  const normalizedViewport = normalizeViewportBounds(viewport);

  if (!normalizedViewport) {
    return null;
  }

  const scale = getViewportScale(normalizedViewport);

  if (!scale) {
    return null;
  }

  const width = viewportArea.right - viewportArea.left;
  const height = viewportArea.bottom - viewportArea.top;

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return {
    scale,
    translateX: viewportArea.left - (viewportArea.left + normalizedViewport.left * width) * scale,
    translateY: viewportArea.top - (viewportArea.top + normalizedViewport.top * height) * scale,
  };
};

export const mapPointToViewport = (point: IViewportPoint, transform: IViewportTransform): IViewportPoint => ({
  x: point.x * transform.scale + transform.translateX,
  y: point.y * transform.scale + transform.translateY,
});

export const mapPointFromViewport = (point: IViewportPoint, transform: IViewportTransform): IViewportPoint => ({
  x: (point.x - transform.translateX) / transform.scale,
  y: (point.y - transform.translateY) / transform.scale,
});

export const getSelectionMode = (
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
  minDragPx: number,
): ISelectionMode | null => {
  const absDx = Math.abs(currentX - startX);
  const absDy = Math.abs(currentY - startY);

  if (absDx >= minDragPx && absDy >= minDragPx) {
    return "xy";
  }

  if (absDx >= minDragPx) {
    return "x";
  }

  if (absDy >= minDragPx) {
    return "y";
  }

  return null;
};

export const getSelectionRect = (
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): ISelectionRect => ({
  left: Math.min(startX, currentX),
  right: Math.max(startX, currentX),
  top: Math.min(startY, currentY),
  bottom: Math.max(startY, currentY),
});

const fitViewportBoundsToChartAspect = (bounds: IViewportZoomBounds): IViewportZoomBounds => {
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;
  const size = Math.min(1, Math.max(width, height));
  const centerX = (bounds.left + bounds.right) / 2;
  const centerY = (bounds.top + bounds.bottom) / 2;
  const left = clamp(centerX - size / 2, 0, 1 - size);
  const top = clamp(centerY - size / 2, 0, 1 - size);

  return {
    left,
    right: left + size,
    top,
    bottom: top + size,
  };
};

export const getViewportSelectionBounds = (
  viewportArea: IViewportAreaLike,
  rect: ISelectionRect,
): IViewportZoomBounds | null => {
  const width = viewportArea.right - viewportArea.left;
  const height = viewportArea.bottom - viewportArea.top;

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  const left = clamp((rect.left - viewportArea.left) / width, 0, 1);
  const right = clamp((rect.right - viewportArea.left) / width, 0, 1);
  const top = clamp((rect.top - viewportArea.top) / height, 0, 1);
  const bottom = clamp((rect.bottom - viewportArea.top) / height, 0, 1);

  if (left >= right || top >= bottom) {
    return null;
  }

  return fitViewportBoundsToChartAspect({ left, right, top, bottom });
};

export const isRadialScale = (scale: unknown): scale is IRadialScaleLike => {
  const candidate = scale as Partial<IRadialScaleLike> | null;

  return (
    !!candidate &&
    Number.isFinite(candidate.xCenter) &&
    Number.isFinite(candidate.yCenter) &&
    Number.isFinite(candidate.drawingArea) &&
    typeof candidate.getValueForDistanceFromCenter === "function"
  );
};

const getMinDistanceFromPointToRect = (x: number, y: number, rect: ISelectionRect): number => {
  const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
  const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;

  return Math.hypot(dx, dy);
};

const getMaxDistanceFromPointToRect = (x: number, y: number, rect: ISelectionRect): number => {
  return Math.max(
    Math.hypot(rect.left - x, rect.top - y),
    Math.hypot(rect.right - x, rect.top - y),
    Math.hypot(rect.left - x, rect.bottom - y),
    Math.hypot(rect.right - x, rect.bottom - y),
  );
};

const isSameBound = (value1: number, value2: number): boolean => {
  return Math.abs(value1 - value2) <= Number.EPSILON * Math.max(1, Math.abs(value1), Math.abs(value2));
};

export const getRadialSelectionBounds = (scale: IRadialScaleLike, rect: ISelectionRect): IAxisZoomBounds | null => {
  const currentMin = Number(scale.min ?? 0);
  const currentMax = Number(scale.max);
  const drawingArea = Number(scale.drawingArea);

  if (!Number.isFinite(currentMin) || !Number.isFinite(currentMax) || !Number.isFinite(drawingArea)) {
    return null;
  }

  const minDistance = clamp(getMinDistanceFromPointToRect(scale.xCenter, scale.yCenter, rect), 0, drawingArea);
  const maxDistance = clamp(getMaxDistanceFromPointToRect(scale.xCenter, scale.yCenter, rect), 0, drawingArea);
  const nextMin = Number(scale.getValueForDistanceFromCenter(minDistance));
  const nextMax = Number(scale.getValueForDistanceFromCenter(maxDistance));
  const min = clamp(Math.min(nextMin, nextMax), currentMin, currentMax);
  const max = clamp(Math.max(nextMin, nextMax), currentMin, currentMax);

  if (
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    min >= max ||
    (isSameBound(min, currentMin) && isSameBound(max, currentMax))
  ) {
    return null;
  }

  return { min, max };
};
